import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    InputAdornment,
    CircularProgress,
    Alert,
    IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
    signInWithPhoneNumber,
    RecaptchaVerifier,
    signOut,
} from 'firebase/auth';
import { auth } from '../../firebase/firebaseConfig';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { authenticatedFetch, publicFetch } from '../../utils/authApi';
import { setNeedsOnboarding, setTrialEnd } from './onboardingSlice';

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:5001'
    : 'https://policytrackr-be-187769890360.asia-south1.run.app');

const LoginAdmin = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState('');
    const [step, setStep] = useState('phone'); // 'phone' or 'otp'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [confirmationResult, setConfirmationResult] = useState(null);

    // Initialize reCAPTCHA on mount
    useEffect(() => {
        try {
            if (!window.recaptchaVerifier) {
                window.recaptchaVerifier = new RecaptchaVerifier(
                    'recaptcha-container',
                    {
                        size: 'invisible',
                        callback: (response) => {
                            // reCAPTCHA solved, allow signInWithPhoneNumber.
                        },
                        'expired-callback': () => {
                            // Response expired. Ask user to solve reCAPTCHA again.
                            setError('reCAPTCHA expired. Please try again.');
                            if (window.recaptchaVerifier) {
                                window.recaptchaVerifier.clear();
                                window.recaptchaVerifier = null;
                            }
                        }
                    },
                    auth
                );
            }
        } catch (err) {
            console.error('Error initializing reCAPTCHA:', err);
        }

        return () => {
            // Cleanup on unmount
            if (window.recaptchaVerifier) {
                try {
                    window.recaptchaVerifier.clear();
                } catch (err) {
                    console.error('Error clearing recaptcha:', err);
                }
                window.recaptchaVerifier = null;
            }
        };
    }, []);

    const handleSendOtp = async (e) => {
        if (e) e.preventDefault();
        setError('');

        if (!phoneNumber || phoneNumber.length < 10) {
            setError('Please enter a valid phone number');
            return;
        }

        setLoading(true);
        try {
            // If already starts with +, use it directly, otherwise add +91
            let formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber}`;
            // --- Pre-OTP Authorization Check ---
            // Call backend API to check if phone number is authorized
            // Note: This endpoint is public (no auth required - it's for login)
            const response = await publicFetch(`${API_BASE_URL}/api/check-admin-auth`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ phoneNumber: formattedPhone }),
            });

            if (!response.ok) {
                throw new Error('Failed to verify authorization. Please try again.');
            }

            const { authorized } = await response.json();

            if (!authorized) {
                setError('This phone number is not authorized for admin access.');
                setLoading(false);
                return;
            }
            // -----------------------------------

            if (!window.recaptchaVerifier) {
                // Try to re-initialize if somehow missing
                try {
                    window.recaptchaVerifier = new RecaptchaVerifier(
                        'recaptcha-container',
                        {
                            size: 'invisible',
                        },
                        auth
                    );
                } catch (recaptchaError) {
                    console.error('Error re-creating RecaptchaVerifier:', recaptchaError);
                    setError('Failed to initialize security verification. Please refresh and try again.');
                    setLoading(false);
                    return;
                }
            }

            const result = await signInWithPhoneNumber(
                auth,
                formattedPhone,
                window.recaptchaVerifier
            );

            setConfirmationResult(result);
            setStep('otp');
            setError('');
        } catch (err) {
            console.error('Error sending OTP:', err);
            if (err.code === 'auth/invalid-phone-number') {
                setError('Invalid phone number format.');
            } else if (err.code === 'auth/too-many-requests') {
                setError('Too many requests. Please try again later.');
            } else if (err.code === 'auth/captcha-check-failed') {
                setError('Security check failed. Please refresh the page and try again, or check your connection.');
            } else if (err.code === 'auth/invalid-app-credential') {
                setError('Localhost Login: You must use a "Test Phone Number" whitelist in Firebase Console (e.g. +1 650-555-1234) to login locally.');
                console.error('⚠️ Firebase Auth: Real phone numbers are blocked on localhost. Go to Firebase Console > Authentication > Sign-in method > Phone > "Phone numbers for testing" and add a test number.');
            } else {
                setError(err.message || 'Failed to send OTP. Please try again.');
            }

            // Do NOT clear recaptchaVerifier here, it might be needed for retry or it threw because of quota
            // But if it's a captcha error, we might want to reset.
            if (err.code === 'auth/captcha-check-failed' && window.recaptchaVerifier) {
                try {
                    window.recaptchaVerifier.render().then(widgetId => {
                        window.recaptchaVerifier.reset(widgetId);
                    });
                } catch (resetErr) {
                    console.warn('Could not reset recaptcha', resetErr);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        if (e) e.preventDefault();
        setError('');

        if (!otp || otp.length !== 6) {
            setError('Please enter a valid 6-digit OTP');
            return;
        }

        if (!confirmationResult) {
            setError('OTP session expired. Please start over.');
            return;
        }

        setLoading(true);
        try {
            console.log('🔐 [LoginAdmin] Verifying OTP...');
            const userCredential = await confirmationResult.confirm(otp);
            const user = userCredential.user;
            console.log('✅ [LoginAdmin] OTP verified successfully. UID:', user.uid);

            // Use backend to sync/migrate admin profile
            console.log('🔄 [LoginAdmin] Calling sync-admin-profile...');
            // Use authenticatedFetch to include Firebase ID token
            const response = await authenticatedFetch(`${API_BASE_URL}/api/sync-admin-profile`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    uid: user.uid,
                    phoneNumber: user.phoneNumber
                }),
            });

            if (!response.ok) {
                console.error('❌ [LoginAdmin] Sync profile failed:', response.status);
                throw new Error('Verification failed on server. Please try again.');
            }

            const result = await response.json();
            console.log('✅ [LoginAdmin] Profile synced result:', result);

            if (result.success) {
                // Store onboarding state in Redux
                if (!result.onboardingComplete) {
                    console.log('📋 [LoginAdmin] Admin needs onboarding');
                    dispatch(setNeedsOnboarding(true));
                }
                if (result.trialEnd) {
                    dispatch(setTrialEnd(result.trialEnd));
                }

                // Wait a bit for server-side triggers if any, then navigate
                console.log('🚀 [LoginAdmin] Authorization confirmed. Navigating in 1.5s...');
                await new Promise(resolve => setTimeout(resolve, 1500));

                // Final check before navigating
                if (!auth.currentUser) {
                    console.error('❌ [LoginAdmin] CRITICAL: User was signed out during wait!');
                }

                navigate('/balance');
            } else {
                console.warn('⚠️ [LoginAdmin] Authorization failed in sync-admin-profile');
                // Unauthorized: Sign out and show error
                await signOut(auth);
                setError('This phone number is not authorized for admin access.');
            }
        } catch (err) {
            console.error('❌ [LoginAdmin] Error during OTP verification/sync:', err);
            if (err.code === 'auth/invalid-verification-code') {
                setError('Invalid OTP. Please check and try again.');
            } else if (err.code === 'auth/code-expired') {
                setError('OTP has expired. Please request a new one.');
                setStep('phone');
                setOtp('');
            } else {
                setError(err.message || 'Failed to verify OTP.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100vh"
            bgcolor="#f0f2f5"
            sx={{ px: { xs: 2, sm: 0 } }}
        >
            <Paper
                elevation={0}
                sx={{
                    padding: { xs: 2, sm: 3 },
                    width: { xs: '100%', sm: 400, md: 440 },
                    maxWidth: '440px',
                    borderRadius: 2,
                    position: 'relative'
                }}
            >
                <IconButton
                    onClick={() => navigate('/login')}
                    sx={{
                        position: 'absolute',
                        left: 16,
                        top: 16,
                        color: '#666'
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>

                {/* Logo Icon */}
                <Box display="flex" justifyContent="center" mb={{ xs: 1.5, sm: 2 }}>
                    <Box
                        sx={{
                            fontSize: { xs: '60px', sm: '72px' },
                            fontWeight: '900',
                            color: '#0051C3',
                            fontFamily: '"Roboto Slab", "Rockwell", "Courier New", "Courier", monospace',
                            lineHeight: 1,
                            letterSpacing: '0.02em',
                            textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                        }}
                    >
                        P
                    </Box>
                </Box>

                <Typography
                    variant="h4"
                    align="center"
                    gutterBottom
                    sx={{
                        fontWeight: 'bold',
                        fontSize: { xs: '1.75rem', sm: '2.125rem' }
                    }}
                >
                    Admin Login
                </Typography>
                <Typography
                    variant="subtitle1"
                    align="center"
                    gutterBottom
                    sx={{
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        mb: { xs: 2, sm: 1 },
                        color: '#666'
                    }}
                >
                    {step === 'phone' ? "Enter your phone number to receive an OTP" : `Enter the 6-digit OTP sent to ${phoneNumber}`}
                </Typography>

                <Box component="form" onSubmit={step === 'phone' ? handleSendOtp : handleVerifyOtp} mt={{ xs: 1, sm: 1 }}>
                    <TextField
                        fullWidth
                        label="Phone Number"
                        variant="outlined"
                        margin="dense"
                        placeholder="10-digit number"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        disabled={loading || step === 'otp'}
                        inputProps={{ maxLength: 16 }}
                        InputProps={{
                            startAdornment: !phoneNumber.startsWith('+') && (
                                <InputAdornment position="start">+91</InputAdornment>
                            ),
                        }}
                        InputLabelProps={{
                            shrink: true,
                            sx: {
                                backgroundColor: 'white',
                                px: 0.5,
                                '&.MuiInputLabel-shrink': {
                                    backgroundColor: 'white',
                                }
                            }
                        }}
                        sx={{
                            "& .MuiInputBase-root": { fontSize: { xs: '0.9rem', sm: '1rem' } },
                            mb: 1
                        }}
                    />

                    {step === 'otp' && (
                        <TextField
                            fullWidth
                            label="OTP Code"
                            variant="outlined"
                            margin="dense"
                            placeholder="000000"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            disabled={loading}
                            inputProps={{
                                maxLength: 6,
                                pattern: '[0-9]*',
                                inputMode: 'numeric',
                            }}
                            InputLabelProps={{
                                shrink: true,
                                sx: {
                                    backgroundColor: 'white',
                                    px: 0.5,
                                    '&.MuiInputLabel-shrink': {
                                        backgroundColor: 'white',
                                    }
                                }
                            }}
                            sx={{
                                "& .MuiInputBase-root": { fontSize: { xs: '0.9rem', sm: '1rem' } },
                                mb: 2
                            }}
                        />
                    )}

                    {step === 'otp' && (
                        <Box display="flex" justifyContent="flex-end" mt={0}>
                            <Button
                                variant="text"
                                size="small"
                                onClick={() => {
                                    setStep('phone');
                                    setOtp('');
                                }}
                                disabled={loading}
                                sx={{
                                    color: '#0051C3',
                                    fontWeight: 500,
                                    fontSize: { xs: '0.85rem', sm: '0.875rem' },
                                    textTransform: 'none',
                                    backgroundColor: 'transparent',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        textDecoration: 'underline',
                                        fontWeight: 600,
                                    }
                                }}
                            >
                                Change phone number
                            </Button>
                        </Box>
                    )}

                    <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loading || (step === 'phone' ? !phoneNumber : otp.length !== 6)}
                        sx={{
                            mt: { xs: 1, sm: 1.5 },
                            py: { xs: 1, sm: 1.2 },
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            backgroundColor: '#0051C3',
                            "&.Mui-disabled": {
                                color: "rgba(0, 0, 0, 0.6)", // Darker text for visibility
                                backgroundColor: "rgba(0, 0, 0, 0.12)"
                            }
                        }}
                    >
                        {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : (step === 'phone' ? "Send OTP" : "Verify & Sign In")}
                    </Button>

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box mt={3} textAlign="center">
                        <Typography variant="body2" sx={{ color: '#666' }}>
                            Want to login as staff?{" "}
                            <Typography
                                component={Link}
                                to="/login-staff"
                                sx={{
                                    color: '#0051C3',
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                        fontWeight: 600
                                    }
                                }}
                            >
                                Click here
                            </Typography>
                        </Typography>
                    </Box>
                </Box>
            </Paper>
            <div id="recaptcha-container"></div>
        </Box>
    );
};

export default LoginAdmin;
